/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { CategoryHierarchy } from '@msdyn365-commerce/commerce-entities';

import { IDobbiesUnbxdRecommendationsData } from './dobbies-unbxd-recommendations.data';
import { IDobbiesUnbxdRecommendationsProps } from './dobbies-unbxd-recommendations.props.autogenerated';

import getUnbxdRecommendationData, {
    GetUnbxdRecommendationsDataInput,
    ICategoryLevelNamesData,
    IGetUnbxdRecommendationData
} from '../../actions/get-unbxd-product-recommendations.action';
import { UnbxdProductCarouselItem } from './components/unbxd-product-carousel-item';
import { getProductPageUrlSync } from '@msdyn365-commerce-modules/retail-actions';
import { removeSpecialChars } from '../../themes/dobbies/utilities/validation';
import { IUnbxdProduct } from './types/unbxd-product';
import { IProductWithModes } from '../dobbies-search-result-container/components';
import { getCartState } from '@msdyn365-commerce/global-state';
import { GetUnbxdAddToCartProductInput } from '../../actions/get-unbxd-add-to-cart-product.action';

interface IDobbiesProductRecommendationCarouselState {
    unbxdRecommendationData: IUnbxdProduct[] | undefined;
    widgetTitle?: string;
    count?: number | undefined;
    unbxdRequestId: string;
    index?: number;
    loading: boolean;
    isModalOpen: boolean;
}
export interface IDobbiesUnbxdRecommendationsViewProps extends IDobbiesUnbxdRecommendationsProps<IDobbiesUnbxdRecommendationsData> {
    carouselItems: React.ReactNode[] | null;
    widgetTitle?: string;
    next: () => void;
    prev: () => void;
    activeIndex: number;
    loading: boolean;
}

/**
 *
 * DobbiesUnbxdRecommendations component
 * @extends {React.PureComponent<IDobbiesUnbxdRecommendationsProps<IDobbiesUnbxdRecommendationsData>>}
 */
class DobbiesUnbxdRecommendations extends React.PureComponent<
    IDobbiesUnbxdRecommendationsProps<IDobbiesUnbxdRecommendationsData>,
    IDobbiesProductRecommendationCarouselState
> {
    constructor(props: IDobbiesUnbxdRecommendationsProps<IDobbiesUnbxdRecommendationsData> & IDobbiesProductRecommendationCarouselState) {
        super(props);
        this.state = {
            unbxdRecommendationData: [],
            widgetTitle: '',
            count: 0,
            unbxdRequestId: '',
            loading: false,
            isModalOpen: false
        };
    }

    public async componentDidMount(): Promise<void> {
        void this._getUnbxdRecommendationsData();
    }

    private _getUnbxdRecommendationsData = async (): Promise<void> => {
        const {
            config: { pageType, widgetNumber, categoryLevelNames, showRemoveProductButton }
        } = this.props;
        const productItemID = this.props.data.product.result?.ItemId;
        const cartItemIDs = this.props.data.cart.result?.cart.CartLines?.map(line => line.LineId);
        this.setState({
            loading: true
        });
        const brand = '';
        try {
            const categoryPaths: ICategoryLevelNamesData[] =
                this.props.data.categoryHierarchy.result?.map(category => {
                    return {
                        categoryLevelName: category.Name
                    };
                }) || [];
            const categoryNames = categoryLevelNames && categoryLevelNames.length > 0 ? categoryLevelNames : categoryPaths;

            // Get unbxd items and fetch inventory only if Add to Basket is enabled
            const responseData: IGetUnbxdRecommendationData = await getUnbxdRecommendationData(
                new GetUnbxdRecommendationsDataInput(pageType, widgetNumber, productItemID, categoryNames, cartItemIDs, brand),
                this.props.context.actionContext
            );
            if (responseData) {
                let filteredRecommendations = responseData.unbxdRecs.recommendations;

                if (showRemoveProductButton) {
                    const cookieKey = 'removed_unbxd_products';
                    const savedData = this.props.context.request.cookies.get(cookieKey);
                    if (savedData && Array.isArray(savedData.value) && savedData.value.length > 0) {
                        filteredRecommendations = filteredRecommendations.filter((p: IUnbxdProduct) => {
                            const widgetKey = `${responseData.unbxdRecs.widgetTitle?.split(' ').join('-')}_${widgetNumber}_${p.uniqueId}`;
                            //@ts-ignore
                            return !savedData.value.includes(widgetKey);
                        });
                    }
                }
                this.setState({
                    count: responseData.unbxdRecs.count,
                    unbxdRecommendationData: filteredRecommendations,
                    widgetTitle: responseData.unbxdRecs.widgetTitle,
                    unbxdRequestId: responseData.unbxdRequestId ? responseData.unbxdRequestId : ''
                });
            }
        } catch (error) {
            console.error('Unbxd API error', error);
        } finally {
            this.setState({
                loading: false
            });
        }
    };

    private _getCategoryHierarchyPath = (category?: CategoryHierarchy): ICategoryLevelNamesData[] => {
        // Add current category
        const path: ICategoryLevelNamesData[] = [{ categoryLevelName: category?.Name || '' }];

        const children = category?.Children;

        // If category has children, recursively add the path of the first child
        if (Array.isArray(children) && children.length > 0) {
            return path.concat(this._getCategoryHierarchyPath(children[0]));
        }

        // No children → end of recursion
        return path;
    };
    private onClickAndCollect = (product: IProductWithModes, setExistsInCartLines: React.Dispatch<React.SetStateAction<boolean>>) => {
        let storeSelectorId = '';
        if (this.props.modules && Object.keys(this.props.modules).length > 0) {
            storeSelectorId = this.props.modules.storeSelectors[0].id;
        }
        this.props.data.storeSelectorStateManager.result?.openDialog({
            id: storeSelectorId,
            product: {
                ...product,
                ProductTypeValue: 0,
                BasePrice: product.Price,
                AdjustedPrice: product.Price
            },
            onLocationSelected: async orgUnitLocation => {
                const cartState = await getCartState(this.props.context.actionContext);
                const productList = new Array();
                productList.push({
                    product: product,
                    count: 1,
                    enableStockCheck: this.props.context.app.config.enableStockCheck,
                    location: orgUnitLocation,
                    deliveryMode: this.props.context.request.channel?.PickupDeliveryModeCode
                });
                await cartState?.addProductsToCart(productList);
                this.props.context.actionContext.update(new GetUnbxdAddToCartProductInput(), { product });
                setExistsInCartLines(true);
                await cartState.refreshCart({});
            }
        });
    };

    public render(): JSX.Element | null {
        const { unbxdRecommendationData } = this.state;
        const viewProps: IDobbiesUnbxdRecommendationsViewProps = {
            ...(this.props as IDobbiesUnbxdRecommendationsProps<IDobbiesUnbxdRecommendationsData>),
            carouselItems: unbxdRecommendationData ? this._getCarouselItems(unbxdRecommendationData) : null,
            activeIndex: this.state.index || 0,
            widgetTitle: this.state.widgetTitle,
            loading: this.state.loading,
            next: this.onNext,
            prev: this.onPrev
        };
        return this.props.renderView(viewProps);
    }

    private onNext = () => {
        const index = Math.min(
            (this.state.index ?? 0) + 1,
            this.state.unbxdRecommendationData?.length ? this.state.unbxdRecommendationData?.length - 1 : 0
        );
        this.setState({ index });
    };
    private onPrev = () => {
        const index = Math.max((this.state.index ?? 0) - 1, 0);
        this.setState({ index });
    };

    private _getCarouselItems = (products: IUnbxdProduct[]): React.ReactNode[] | null => {
        if (products.length === 0) {
            return null;
        }
        const { context } = this.props;
        const { widgetTitle } = this.state;
        const widgetNumber = this.props.config.widgetNumber;
        const showRemoveProductButton = this.props.config.showRemoveProductButton;

        return products.map((product, index) => {
            const productUrl = getProductPageUrlSync(
                removeSpecialChars(product?.Name!, context) || '',
                product.RecordId,
                context && this.props.context.actionContext,
                undefined
            );
            return (
                <UnbxdProductCarouselItem
                    key={product.uniqueId}
                    position={index}
                    context={context}
                    product={product}
                    productUrl={productUrl}
                    typeName={this.props.typeName}
                    showAddToBasket={this.props.config.showAddToBasket}
                    showRemoveProductButton={showRemoveProductButton}
                    onClickAndCollect={this.onClickAndCollect}
                    removedProductFromCart={this.props.data.unbxdAddedToCartProduct?.result?.product}
                    removeProduct={() => {
                        this.setState(prevState => {
                            const updatedProducts = prevState.unbxdRecommendationData?.filter(p => p.uniqueId !== product.uniqueId);

                            if (showRemoveProductButton) {
                                const cookieKey = 'removed_unbxd_products';
                                const savedData = this.props.context.request.cookies.get(cookieKey);
                                const formattedWidgeTitle = widgetTitle?.split(' ').join('-');
                                const widgetKey = `${formattedWidgeTitle}_${widgetNumber}_${product.uniqueId}`;

                                if (savedData && Array.isArray(savedData.value)) {
                                    savedData.value.push(widgetKey);
                                } else {
                                    savedData.value = [widgetKey];
                                }
                                const days = this.props.config.expiryDays;
                                this.props.context.request.cookies.set(cookieKey, savedData.value, {
                                    expires: new Date(new Date().getTime() + days * 24 * 60 * 60 * 1000)
                                });
                            }

                            return { unbxdRecommendationData: updatedProducts };
                        });
                    }}
                />
            );
        });
    };
}

export default DobbiesUnbxdRecommendations;
